import React from "react";
import PropTypes from "kf-proptypes/widgets";

import { SignaturePopup } from "./signaturepopup.js";
import { FieldLabel, FieldHint, FieldErrors } from "../fields/index.jsx";
import { EditableSignature } from "./edtiable.jsx";
import { ReadonlySignature } from "./readonly.jsx";

export { SignaturePopup };
export function Signature({
  value,
  readOnly,
  field,
  updateStatus,
  onChange,
  onFail,
  prefix,
  enableAttachment = false,
  handleAttachment = () => {},
  onFileAttached = () => {},
  isError,
  isPublicForm,
  id,
  label,
  required,
  disabled,
  hint,
  errors,
  rowAsList,
  allowDraw = true,
  labelLeftIcon,
  labelRightIcon,
  queryKey,
  publicSignatureUrl
}) {
  return (
    <React.Fragment>
      {label && (
        <FieldLabel
          label={label}
          required={required}
          targetId={id}
          disabled={disabled}
          leftIcon={labelLeftIcon}
          rightIcon={labelRightIcon}
        />
      )}
      {readOnly ? (
        <ReadonlySignature
          field={field}
          errors={errors}
          value={value}
          isError={isError}
          isPublicForm={isPublicForm}
          rowAsList={rowAsList}
          publicSignatureUrl={publicSignatureUrl}
        />
      ) : (
        <EditableSignature
          value={value}
          field={field}
          updateStatus={updateStatus}
          onChange={onChange}
          onFail={onFail}
          prefix={prefix}
          enableAttachment={enableAttachment}
          handleAttachment={handleAttachment}
          isError={isError}
          isPublicForm={isPublicForm}
          errors={errors}
          rowAsList={rowAsList}
          allowDraw={allowDraw}
          onFileAttached={onFileAttached}
          queryKey={queryKey}
          publicSignatureUrl={publicSignatureUrl}
        />
      )}
      {hint && <FieldHint hint={hint} targetId={id} />}
      {errors && errors.length > 0 && (
        <FieldErrors errors={errors} targetId={id} />
      )}
    </React.Fragment>
  );
}

Signature.propTypes = {
  /** A callback function that get invoked when the signature changes. */
  onChange: PropTypes.func,
  /** Trigger when signature upload is failed */
  onFail: PropTypes.func,
  /** Trigger when signature upload is failed */
  value: PropTypes.shape({
    name: PropTypes.string,
    key: PropTypes.string
  }),
  /** Updated the status of the content */
  updateStatus: PropTypes.func,
  /** Show the signature component in readOnly mode, the user won't be able to change his/her signature. */
  readOnly: PropTypes.bool,
  /** Field to determine the value */
  field: PropTypes.shape({
    Id: PropTypes.string.isRequired,
    IsHidden: PropTypes.bool.isRequired,
    IsProtected: PropTypes.bool,
    IsSystemField: PropTypes.bool,
    ModelId: PropTypes.string,
    Model: PropTypes.string,
    Name: PropTypes.string.isRequired,
    Required: PropTypes.bool.isRequired,
    Type: PropTypes.string.isRequired,
    Visible: PropTypes.bool,
    disableEdit: PropTypes.bool,
    enableUpload: PropTypes.bool
  }),
  /** Prefix is used to image loading */
  prefix: PropTypes.string,
  /** Is this signature component in error state? isError is automatically true if error.length > 0 */
  isError: PropTypes.bool,
  /** Attachment will be enabled based on this prop */
  enableAttachment: PropTypes.bool,
  /** Trigger when attachment is enabled */
  handleAttachment: PropTypes.func,
  /** Enable public form option */
  isPublicForm: PropTypes.bool,
  id: PropTypes.string,
  /** A text to display near the signature component as a label. */
  label: PropTypes.string,
  /** Is the signature mandatory? */
  required: PropTypes.bool,
  /** Signature  */
  disabled: PropTypes.bool,
  /** Show hint about the signature below the signature form control. */
  hint: PropTypes.string,
  /** Displays the roles as list */
  rowAsList: PropTypes.shape({
    tableRowId: PropTypes.string,
    field: PropTypes.shape({
      Id: PropTypes.string
    }),
    tableId: PropTypes.string
  }),
  /** A list of errors to be displayed below the signature form control. */
  errors: PropTypes.arrayOf(PropTypes.string),
  /**
   * Allow the user to draw his / her signature using the component,
   * if set to false, a modal with the message
   * "You cannot draw a signature. Add a signature in your&nbsp;
   * About me section to use it here." Will show up.
   */
  allowDraw: PropTypes.bool,
  /** it will render left icon of label field */
  labelLeftIcon: PropTypes.node,
  /** it will render right icon of label field */
  labelRightIcon: PropTypes.node,
  onFileAttached: PropTypes.func,
  queryKey: PropTypes.arrayOf(PropTypes.string),
  publicSignatureUrl: PropTypes.string
};

Signature.defaultProps = {
  onChange: () => {},
  onFail: () => {},
  updateStatus: () => {},
  field: {},
  enableAttachment: false,
  handleAttachment: () => {},
  errors: [],
  allowDraw: true
};
